import React, { useState, useEffect } from "react";
import styles from "./NewsPage.module.css";
import Footer from "../components/Footer";
import {
  Flex,
  Text,
  Box,
  Heading,
  Spinner,
  Image,
  SimpleGrid,
  Link as ChakraLink,
  Button,
  Badge,
  Container,
  HStack,
  Icon,
  useColorModeValue,
  Divider,
  Tag,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { getNewsMetadata } from "../utils/newsUtils";
import { CalendarIcon } from "@chakra-ui/icons";

const NewsPage = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [featuredNews, setFeaturedNews] = useState(null);
  const [otherNews, setOtherNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tagBg = useColorModeValue("blue.50", "blue.900");
  const dividerColor = useColorModeValue("gray.200", "gray.600");
  const featureBg = useColorModeValue("gray.50", "gray.800");

  const textSize = useBreakpointValue({ base: "sm", md: "md" });

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const metadata = await getNewsMetadata();
        setNewsItems(metadata);

        const openingArticle = metadata.find(
          (article) => article.slug === "first-article"
        );

        if (openingArticle) {
          // Set the opening article as featured
          setFeaturedNews(openingArticle);
          // Set all other articles (excluding the featured one) as otherNews
          setOtherNews(
            metadata.filter((article) => article.slug !== "first-article")
          );
        } else if (metadata.length > 0) {
          // Fallback to the first article if opening announcement not found
          setFeaturedNews(metadata[0]);
          setOtherNews(metadata.slice(1));
        }

        setError(null);
      } catch (err) {
        console.error("Failed to load news:", err);
        setError("Failed to load news items. Please try again later.");
        setNewsItems([]);
        setFeaturedNews(null);
        setOtherNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  return (
    <Flex direction="column" minHeight="100vh" bg={bgColor}>
      <Box flex="1" className={styles.pageContainer} p={{ base: 4, md: 0 }}>
        <Container maxW="6xl" py={10}>
          <Box textAlign="center" mb={12}>
            <Text
              color="gray.400"
              fontWeight="bold"
              letterSpacing="wide"
              fontSize="sm"
              textTransform="uppercase"
            >
              Stay Informed
            </Text>
            <Heading as="h1" size="2xl" mb={4} bgColor="#EA2E01" bgClip="text">
              Latest News & Updates
            </Heading>
            <Text fontSize="lg" color="gray.500" maxW="xl" mx="auto">
              Discover the latest happenings, announcements, and company updates
            </Text>
          </Box>

          {loading && (
            <Flex justify="center" align="center" height="300px">
              <Spinner size="xl" thickness="4px" color="blue.500" />
            </Flex>
          )}

          {error && (
            <Box
              textAlign="center"
              p={6}
              borderRadius="lg"
              borderWidth="1px"
              borderColor="red.300"
              bg="red.50"
            >
              <Text color="red.500" fontSize="lg">
                {error}
              </Text>
            </Box>
          )}

          {!loading && !error && newsItems.length === 0 && (
            <Box
              textAlign="center"
              p={10}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Text fontSize="lg" color="gray.500">
                No news articles found.
              </Text>
            </Box>
          )}

          {!loading && !error && featuredNews && (
            <Box mb={16}>
              <Flex align="center" mb={6}>
                <Divider flex="1" borderColor={dividerColor} />
                <Text
                  px={6}
                  fontWeight="bold"
                  fontSize="lg"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Featured Story
                </Text>
                <Divider flex="1" borderColor={dividerColor} />
              </Flex>

              <Box
                borderRadius="xl"
                overflow="hidden"
                boxShadow="xl"
                bg={featureBg}
                position="relative"
              >
                <Flex direction={{ base: "column", lg: "row" }}>
                  <Box position="relative" width={{ base: "100%", lg: "50%" }}>
                    {featuredNews.thumbnail ? (
                      <Image
                        src={featuredNews.thumbnail}
                        alt={`${featuredNews.title} thumbnail`}
                        objectFit="cover"
                        height="100%"
                        width="100%"
                      />
                    ) : (
                      <Box
                        height="100%"
                        width="100%"
                        bg="blue.100"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="lg" color="blue.500">
                          No Image Available
                        </Text>
                      </Box>
                    )}
                    <Badge
                      position="absolute"
                      top="4"
                      left="4"
                      colorScheme="blue"
                      fontSize="sm"
                      borderRadius="full"
                      px={3}
                      py={1}
                      boxShadow="md"
                    >
                      Latest
                    </Badge>
                  </Box>

                  <Box
                    p={{ base: 6, md: 8 }}
                    width={{ base: "100%", lg: "50%" }}
                  >
                    <Heading
                      as="h2"
                      fontSize={{ base: "2xl", md: "3xl" }}
                      lineHeight="1.2"
                      mb={4}
                    >
                      {featuredNews.title}
                    </Heading>

                    <HStack spacing={4} mb={5} color="gray.500">
                      <Flex align="center">
                        <Icon as={CalendarIcon} mr={1} />
                        <Text fontSize="sm">
                          {new Date(featuredNews.date).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </Text>
                      </Flex>
                      <Flex align="center">
                        <Text fontSize="sm">
                          {featuredNews.author || "Admin"}
                        </Text>
                      </Flex>
                    </HStack>

                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      mb={6}
                      color="gray.600"
                      lineHeight="tall"
                    >
                      {featuredNews.snippet}
                    </Text>

                    <ChakraLink
                      as={RouterLink}
                      to={`/news/${featuredNews.slug}`}
                    >
                      <Button size="lg" borderRadius="full" px={8}>
                        Read Full Story
                      </Button>
                    </ChakraLink>
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          {!loading && !error && otherNews.length > 0 && (
            <Box>
              <Flex align="center" mb={10}>
                <Divider flex="1" borderColor={dividerColor} />
                <Text
                  px={6}
                  fontWeight="bold"
                  fontSize="lg"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  More Articles
                </Text>
                <Divider flex="1" borderColor={dividerColor} />
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                {otherNews.map((item) => (
                  <Box
                    key={item.slug}
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="md"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    transition="all 0.3s ease"
                    _hover={{
                      transform: "translateY(-8px)",
                      boxShadow: "xl",
                      borderColor: "blue.200",
                    }}
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    onClick={() => {
                      window.location.href = `/news/${item.slug}`;
                    }}
                  >
                    <Box position="relative">
                      {item.thumbnail ? (
                        <Image
                          src={item.thumbnail}
                          alt={`${item.title} thumbnail`}
                          objectFit="cover"
                          height="220px"
                          width="100%"
                        />
                      ) : (
                        <Box
                          height="220px"
                          width="100%"
                          bg="blue.50"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text color="blue.500">No Image</Text>
                        </Box>
                      )}
                      <Box
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        bg="rgba(0,0,0,0.6)"
                        p={2}
                      >
                        <HStack spacing={2}>
                          <Tag size="sm" colorScheme="blue" borderRadius="full">
                            News
                          </Tag>
                          <Tag size="sm" bg={tagBg} borderRadius="full">
                            {new Date(item.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </Tag>
                        </HStack>
                      </Box>
                    </Box>

                    <Box p={5} flex="1" display="flex" flexDirection="column">
                      <Heading as="h3" size="md" mb={3} lineHeight="tight">
                        {item.title}
                      </Heading>

                      <Text fontSize="sm" color="gray.500" mb={3}>
                        By {item.author || "Admin"}
                      </Text>

                      <Text
                        noOfLines={3}
                        mb={4}
                        flex="1"
                        fontSize={textSize}
                        color="gray.600"
                      >
                        {item.snippet}
                      </Text>

                      <ChakraLink
                        as={RouterLink}
                        to={`/news/${item.slug}`}
                        mt="auto"
                        _hover={{ textDecoration: "none" }}
                      >
                        <Button
                          variant="ghost"
                          colorScheme="blue"
                          size="sm"
                          rightIcon={
                            <Box as="span" ml={1}>
                              →
                            </Box>
                          }
                          _hover={{
                            bg: "blue.50",
                            transform: "translateX(4px)",
                          }}
                        >
                          Continue Reading
                        </Button>
                      </ChakraLink>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </Flex>
  );
};

export default NewsPage;
